import React, { ChangeEvent, useState } from 'react';
import { DataTable } from '@/components/shared';
import { List } from '@refinedev/antd';
import { endPoints } from '@/api/configs';
import { useList } from '@/hooks/useList';
import { useTranslation } from '@refinedev/core';
import { PlusOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { getColumns } from './columns';
import { Form, Input, Button, Space, Select, message } from 'antd';
import { AddAdminModal } from '@/components/modals';
import { AdminRes } from '@/api/types/getDataTypes';
import { admin_type } from '@/api/types/getAdmin';
import useApi from '@/api/useApi';
import { EditAdminModal } from '@/components/modals/EditAdminModal';
import { useProfileStore } from '@/core';

export const AdminList = () => {
  const { translate } = useTranslation();
  const { userData } = useProfileStore();
  const adminType = userData?.account?.admin?.admin_type;
  const [deactivateId, setDeactivateId] = useState<string>('');
  const [editAdminData, setEditAdminData] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] =
    useState<boolean>(false);
  const [isEditAdminModalOpen, setIsEditAdminModalOpen] =
    useState<boolean>(false);
  const [form] = Form.useForm();
  const {
    data,
    isLoading,
    page,
    setPage,
    isRefetching,
    setSearch,
    refetch,
    setFilters,
    resolvedTotal,
  } = useList<{
    data: AdminRes[];
  }>({
    endpoint: endPoints.admin(),
    filters: {
      status: undefined,
    },
  });

  const { usePatch } = useApi();
  const { mutate: deactivateAdmin, isLoading: isLoadingDeactivate } = usePatch(
    endPoints.admin(deactivateId)
  );
  const handleDeactivate = (is_active: boolean) => {
    deactivateAdmin(
      { is_active: !is_active },
      {
        onSuccess: () => {
          message.success(translate('messages.success-delete-admin'));
          refetch();
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };
  const columns = getColumns(
    translate,
    isLoadingDeactivate,
    handleDeactivate,
    setDeactivateId,
    setEditAdminData,
    setIsEditAdminModalOpen,
    adminType,
    deactivateId
  );

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearch(e.target.value);
  };
  const handleSelectRole = (value: admin_type) => {
    setPage(1);
    setFilters({
      admin_type: value,
    });
  };
  const handleClearFilters = () => {
    form.resetFields();
    setFilters({
      search: undefined,
      admin_type: undefined,
    });
  };
  return (
    <List
      title={
        <div className="flex gap-x-3">
          <UserOutlined />
          <h1>{translate('listing-admins.title')}</h1>
        </div>
      }
      canCreate={false}
      headerButtonProps={{
        hidden: adminType !== admin_type.SUPER_ADMIN,
      }}
      headerButtons={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddAdminModalOpen(true)}
          >
            {translate('buttons.create')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <div className="flex flex-wrap gap-x-3">
          <Form.Item name="search" className="w-full sm:w-1/6 min-w-[200px]">
            <Input
              placeholder={translate('search-admin.fields.search')}
              onChange={onSearch}
            />
          </Form.Item>
          <Form.Item
            name="admin_type"
            className="w-full sm:w-1/6 min-w-[200px]"
          >
            <Select
              placeholder={translate('listing-admins.role.placeholder')}
              onChange={handleSelectRole}
              options={Object.values(admin_type).map((type) => ({
                label: translate(`listing-admins.role.${type}`),
                value: type,
              }))}
            />
          </Form.Item>
          <Button
            className="mb-2 sm:mb-0"
            type="primary"
            onClick={handleClearFilters}
            icon={<ReloadOutlined />}
          />
        </div>
      </Form>
      <DataTable
        currentPage={page}
        onPageChange={setPage}
        columns={columns}
        loading={isLoading || isRefetching}
        data={{
          data: data?.data.data || [],
          headers: {
            'x-total-count': resolvedTotal,
          },
        }}
      />
      <AddAdminModal
        open={isAddAdminModalOpen}
        onClose={() => setIsAddAdminModalOpen(false)}
        refetch={refetch}
      />
      <EditAdminModal
        open={isEditAdminModalOpen}
        onClose={() => setIsEditAdminModalOpen(false)}
        refetch={refetch}
        adminId={editAdminData?.id || ''}
        initialValues={{ name: editAdminData?.name || '' }}
      />
    </List>
  );
};
